import { useState, useEffect, useCallback } from 'react';
import MyApiClient from '../api-client/MyApiClient';

// Cache globale condivisa tra tutti i componenti
const adminBadgesCache = {
  unpaidCount: null,
  rejectedCount: null,
  timestamp: null,
  isLoading: false
};

const listeners = new Set();

const notifyListeners = (data) => {
  listeners.forEach(listener => listener(data));
};

// Cache di 2 minuti, allineata con useMemberships
const CACHE_DURATION = 2 * 60 * 1000;

export const useAdminBadges = ({ autoFetch = false } = {}) => {
  const [unpaidCount, setUnpaidCount] = useState(adminBadgesCache.unpaidCount ?? 0);
  const [rejectedCount, setRejectedCount] = useState(adminBadgesCache.rejectedCount ?? 0);
  const [loading, setLoading] = useState(false);

  const getToken = useCallback(() => {
    const tokenDetailsString = sessionStorage.getItem('userDetails');
    if (tokenDetailsString) {
      const tokenDetails = JSON.parse(tokenDetailsString);
      return tokenDetails.idToken;
    }
    return null;
  }, []);

  const isCacheValid = useCallback(() => {
    if (adminBadgesCache.unpaidCount === null || !adminBadgesCache.timestamp) return false;
    return Date.now() - adminBadgesCache.timestamp < CACHE_DURATION;
  }, []);

  const fetchBadges = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    if (isCacheValid()) {
      setUnpaidCount(adminBadgesCache.unpaidCount);
      setRejectedCount(adminBadgesCache.rejectedCount);
      return;
    }

    if (adminBadgesCache.isLoading) return;

    try {
      adminBadgesCache.isLoading = true;
      setLoading(true);

      const apiClient = new MyApiClient();

      // Chiamata leggera: limit=1 serve solo per leggere numberOfItems
      const [bookingsData, transactionsData] = await Promise.all([
        apiClient.getUserBookings(token, 1, 0, 'UNPAID'),
        apiClient.getUserBookingTransactions(token)
      ]);

      const unpaid = bookingsData?.numberOfItems ?? 0;
      // Documenti non risolti = solo le FATTURE che non sono ancora ACCEPTED dall'AE
      // (NOT_SENT/nero, SENT/giallo in attesa, REJECTED/rosso). NOT_GENERATED escluso
      // perché filtrato anche dalla tabella. Le ricevute (type === 'RECEIPT') non
      // hanno un flusso SDI e non concorrono al conteggio.
      const rejected = Array.isArray(transactionsData)
        ? transactionsData.filter(t =>
            t.type === 'INVOICE' &&
            t.invoice_status !== 'ACCEPTED' &&
            t.invoice_status !== 'NOT_GENERATED'
          ).length
        : 0;

      adminBadgesCache.unpaidCount = unpaid;
      adminBadgesCache.rejectedCount = rejected;
      adminBadgesCache.timestamp = Date.now();

      setUnpaidCount(unpaid);
      setRejectedCount(rejected);
      notifyListeners({ unpaidCount: unpaid, rejectedCount: rejected });
    } catch (err) {
      console.error('Errore nel caricamento dei badge admin:', err);
    } finally {
      adminBadgesCache.isLoading = false;
      setLoading(false);
    }
  }, [getToken, isCacheValid]);

  const invalidateCache = useCallback(() => {
    adminBadgesCache.unpaidCount = null;
    adminBadgesCache.rejectedCount = null;
    adminBadgesCache.timestamp = null;
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchBadges();
    }
  }, [autoFetch, fetchBadges]);

  useEffect(() => {
    const listener = ({ unpaidCount: u, rejectedCount: r }) => {
      setUnpaidCount(u);
      setRejectedCount(r);
    };
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  return {
    unpaidCount,
    rejectedCount,
    loading,
    fetchBadges,
    invalidateCache
  };
};

export default useAdminBadges;
